import type { ApiError } from "@positive/api-client";

import { authPaths } from "./config";
import { readTokenClaims, realmRolesOf } from "./token";

/** The parts of an oidc-client-ts `User` the app uses. */
export interface AuthUser {
  access_token: string;
  id_token?: string | undefined;
  refresh_token?: string | undefined;
  expired?: boolean | undefined;
  profile: {
    sub: string;
    name?: string | undefined;
    preferred_username?: string | undefined;
    email?: string | undefined;
  };
  state?: unknown;
}

/** The parts of oidc-client-ts' `UserManager` the app uses (a port, so the service is testable without Keycloak). */
export interface OidcPort {
  getUser: () => Promise<AuthUser | null>;
  removeUser: () => Promise<void>;
  signinRedirect: (args?: {
    state?: unknown;
    prompt?: string;
    /** OIDC `ui_locales`: the language of Keycloak's login page. */
    ui_locales?: string;
    extraQueryParams?: Record<string, string>;
  }) => Promise<void>;
  signinCallback: (url?: string) => Promise<AuthUser | undefined>;
  /** Popup page: hands the response URL to the opener, which validates it against its own stored request. */
  signinPopupCallback: (url?: string) => Promise<void>;
  /** Silent-renew iframe page: hands the response URL to the parent. */
  signinSilentCallback: (url?: string) => Promise<void>;
  signinSilent: () => Promise<AuthUser | null>;
  signinPopup: (args?: { prompt?: string; max_age?: number }) => Promise<AuthUser>;
  signoutRedirect: (args?: { id_token_hint?: string; post_logout_redirect_uri?: string }) => Promise<void>;
  events: {
    addUserLoaded: (callback: (user: AuthUser) => void) => void;
    addUserUnloaded: (callback: () => void) => void;
    addAccessTokenExpired: (callback: () => void) => void;
  };
}

/** Why the user is signed out (shown on the signed-out page). */
export type SignOutReason = "manual" | "idle" | "expired";

export const signOutReasons: readonly SignOutReason[] = ["manual", "idle", "expired"];

export type AuthState =
  | { status: "starting" }
  | { status: "redirecting" }
  /** A callback page (silent renew iframe or popup) — the app itself must not render. */
  | { status: "callback" }
  | { status: "authenticated"; user: AuthUser; roles: readonly string[] }
  | { status: "signed-out"; reason: SignOutReason }
  | { status: "error"; code: string };

/** Result of a Keycloak application-initiated action (e.g. change password), shown once after the redirect. */
export type KcActionStatus = "success" | "cancelled" | "error";

interface ReturnState {
  returnTo: string;
}

function isReturnState(value: unknown): value is ReturnState {
  return typeof value === "object" && value !== null && typeof (value as ReturnState).returnTo === "string";
}

export const signedOutParam = "signed-out";

/** Session errors that end the session instead of renewing the token (identity contract §4). */
const sessionEndCodes: Readonly<Record<string, SignOutReason>> = {
  session_idle_expired: "idle",
  session_expired: "expired",
};

export interface AuthServiceOptions {
  oidc: OidcPort;
  /** `window.location`-like source of the current URL. */
  location: { href: string; origin: string; pathname: string; search: string; hash: string };
  /** Replaces the URL without navigation (`history.replaceState`). */
  replaceUrl: (url: string) => void;
  /** Language last used in this browser, sent as `ui_locales` so Keycloak's login page matches (ADR-0009/D-6). */
  uiLocale?: () => string | undefined;
}

/**
 * OIDC session of the SPA (ADR-0003/D-2): Authorization Code + PKCE via oidc-client-ts, tokens in memory only (a
 * reload signs in again through the Keycloak SSO session), silent renewal with the rotating refresh token, sign-out
 * through Keycloak's end-session endpoint. Holds no business state: permissions and branch come from the API.
 */
export class AuthService {
  private state: AuthState = { status: "starting" };
  private readonly listeners = new Set<() => void>();
  private renewal: Promise<boolean> | null = null;
  private kcAction: KcActionStatus | null = null;
  private readonly oidc: OidcPort;
  private readonly location: AuthServiceOptions["location"];
  private readonly replaceUrl: AuthServiceOptions["replaceUrl"];
  private readonly uiLocale: AuthServiceOptions["uiLocale"];

  constructor({ oidc, location, replaceUrl, uiLocale }: AuthServiceOptions) {
    this.oidc = oidc;
    this.location = location;
    this.replaceUrl = replaceUrl;
    this.uiLocale = uiLocale;
    oidc.events.addUserLoaded((user) => {
      this.setAuthenticated(user);
    });
    oidc.events.addUserUnloaded(() => {
      if (this.state.status === "authenticated") this.setState({ status: "signed-out", reason: "expired" });
    });
    oidc.events.addAccessTokenExpired(() => {
      void this.renew();
    });
  }

  getState = (): AuthState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Outcome of a Keycloak action (change password) after its redirect; read once. */
  takeKcActionStatus(): KcActionStatus | null {
    const status = this.kcAction;
    this.kcAction = null;
    return status;
  }

  /** Handles the redirect callback, restores an in-memory session or starts the sign-in redirect. */
  async start(): Promise<AuthState> {
    const { pathname, search } = this.location;
    const params = new URLSearchParams(search);
    if (pathname === authPaths.silentCallback || pathname === authPaths.popupCallback) {
      // The request state lives in the opener's/parent's session storage, not in this window's.
      this.setState({ status: "callback" });
      const callback =
        pathname === authPaths.popupCallback ? this.oidc.signinPopupCallback : this.oidc.signinSilentCallback;
      await callback.call(this.oidc, this.location.href).catch(() => undefined);
      return this.state;
    }
    if (pathname === authPaths.callback) return this.completeSignIn(params);

    const reason = params.get(signedOutParam);
    if (reason !== null) {
      this.setState({
        status: "signed-out",
        reason: (signOutReasons as readonly string[]).includes(reason) ? (reason as SignOutReason) : "manual",
      });
      return this.state;
    }

    const user = await this.oidc.getUser().catch(() => null);
    if (user && !user.expired) {
      this.setAuthenticated(user);
      return this.state;
    }
    await this.signIn();
    return this.state;
  }

  /** Starts the sign-in redirect, returning to `returnTo` (default: the current page). */
  async signIn(returnTo = this.currentPath()): Promise<void> {
    this.setState({ status: "redirecting" });
    await this.oidc.signinRedirect({ state: { returnTo } satisfies ReturnState, ...this.uiLocales() });
  }

  /** Keycloak's own "update password" page (application-initiated action), then back to the current page. */
  async changePassword(): Promise<void> {
    this.setState({ status: "redirecting" });
    await this.oidc.signinRedirect({
      state: { returnTo: this.currentPath() } satisfies ReturnState,
      ...this.uiLocales(),
      extraQueryParams: { kc_action: "UPDATE_PASSWORD" },
    });
  }

  /** Signs out at Keycloak (end-session) and drops the in-memory tokens. */
  async signOut(reason: SignOutReason = "manual"): Promise<void> {
    const user = this.state.status === "authenticated" ? this.state.user : null;
    this.setState({ status: "redirecting" });
    await this.oidc.removeUser().catch(() => undefined);
    await this.oidc.signoutRedirect({
      ...(user?.id_token ? { id_token_hint: user.id_token } : {}),
      post_logout_redirect_uri: `${this.location.origin}/?${signedOutParam}=${reason}`,
    });
  }

  /**
   * Re-authentication after the back-office idle lock (P4): Keycloak's login in a popup, so the work underneath stays
   * in memory. Resolves `false` when the popup is blocked or closed; the caller then offers a full sign-in.
   */
  async reauthenticate(): Promise<boolean> {
    try {
      const user = await this.oidc.signinPopup({ prompt: "login", max_age: 0 });
      this.setAuthenticated(user);
      return true;
    } catch {
      return false;
    }
  }

  /** Current access token; waits for a renewal in flight. */
  getAccessToken = async (): Promise<string | null> => {
    if (this.renewal) await this.renewal;
    return this.state.status === "authenticated" ? this.state.user.access_token : null;
  };

  /**
   * 401 from the API: session-ending codes sign out; otherwise one silent renewal (refresh token) is tried and the
   * request is retried with the new token. If renewal fails, the user signs in again and comes back to this page.
   */
  handleUnauthorized = async (error: ApiError): Promise<boolean> => {
    const reason = sessionEndCodes[error.code];
    if (reason) {
      if (this.state.status === "authenticated") await this.signOut(reason);
      return false;
    }
    if (this.state.status !== "authenticated") return false;
    if (await this.renew()) return true;
    await this.signIn();
    return false;
  };

  private renew(): Promise<boolean> {
    this.renewal ??= this.oidc
      .signinSilent()
      .then((user) => {
        if (!user) return false;
        this.setAuthenticated(user);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        this.renewal = null;
      });
    return this.renewal;
  }

  private async completeSignIn(params: URLSearchParams): Promise<AuthState> {
    const error = params.get("error");
    if (error !== null) {
      this.replaceUrl("/");
      this.setState({ status: "error", code: error });
      return this.state;
    }
    const action = params.get("kc_action_status");
    if (action === "success" || action === "cancelled" || action === "error") this.kcAction = action;
    try {
      const user = await this.oidc.signinCallback(this.location.href);
      if (!user) throw new Error("no user");
      this.replaceUrl(isReturnState(user.state) && user.state.returnTo.startsWith("/") ? user.state.returnTo : "/");
      this.setAuthenticated(user);
    } catch {
      // Stale or replayed callback (e.g. the back button): start over instead of showing an error.
      this.replaceUrl("/");
      await this.signIn("/");
    }
    return this.state;
  }

  private uiLocales(): { ui_locales?: string } {
    const locale = this.uiLocale?.();
    return locale ? { ui_locales: locale } : {};
  }

  private currentPath(): string {
    const { pathname, search, hash } = this.location;
    const params = new URLSearchParams(search);
    params.delete(signedOutParam);
    const query = params.toString();
    const path = Object.values(authPaths).includes(pathname as never) ? "/" : pathname;
    return `${path}${query ? `?${query}` : ""}${hash}`;
  }

  private setAuthenticated(user: AuthUser): void {
    this.setState({ status: "authenticated", user, roles: realmRolesOf(readTokenClaims(user.access_token)) });
  }

  private setState(state: AuthState): void {
    this.state = state;
    for (const listener of this.listeners) listener();
  }
}
